/**
 * Build-time feature flags.
 *
 * Mirrors the backend's `app.features.*` block and the twin files in
 * `apps/management` and `apps/web`. A flag must be off on BOTH sides — the
 * backend gates the routes so they 404, this hides the surface that would
 * call them. Turning off only one leaves a visible button that errors.
 *
 * As with every `VITE_*` var these are baked into the bundle at build time
 * and are public. Flags only — never a secret, never a permission check.
 */

/**
 * Technician-initiated parts — **withheld from this release.**
 *
 * The shop workflow changed: a technician who needs a part walks over and
 * tells the manager in person, outside the system. The manager then adds it
 * to the work order. So every way a technician could put a part onto a job
 * from this app is hidden:
 *
 *   - the "+ Part" button on the service header and on the bottom action bar,
 *     with their catalog popover and confirm dialog
 *   - the filed part-requests strip under a service (status + withdraw)
 *   - the Inventory "Reorder material" flow — the same part-request endpoint,
 *     just `kind=INVENTORY` instead of `WORK_ORDER`
 *
 * This used to cover BOTH paths the picker took: filing a request for
 * management to accept, and the direct-add shortcut technicians holding the
 * DIRECT_ADD grant got instead. **The second path no longer exists at all.**
 * The grant was removed — it was the one way billable work reached a job
 * with no manager approval — so `POST /technical/work-orders/{id}/parts`
 * refuses unconditionally, and its client (`useAddServicePart`,
 * `workshopApi.addServicePart`, the `PickPartRequest` type) is deleted
 * rather than left as a caller for a route that can only 403.
 * `usePartAddConfirm` files a request and nothing else.
 *
 * So this flag now gates ONE path, not two, and flipping it brings back the
 * request queue only.
 *
 * Two things deliberately stay:
 *   - the read-only PARTS list on a service. A technician must still SEE what
 *     the manager attached; only putting parts there goes away.
 *   - `proposeService`, which is how a technician raises work found mid-job.
 *     It lands PENDING_APPROVAL for the manager to price and the customer to
 *     answer — the approval flow that made DIRECT_ADD redundant.
 *
 * The manager's own add-part UI in `apps/management` is untouched — it is
 * now the only way a part reaches a work order, so gating it would leave
 * nobody able to do it at all.
 *
 * The request path itself is not deleted: `ServicePartButton`,
 * `PartPickerPopover`, `usePartAddConfirm`, `ServicePartRequests`,
 * `MaterialReorderModal`, their hooks in `useStaff.ts` and all of their
 * tests remain, so bringing it back is flipping flags rather than
 * rebuilding it. (They were `TaskPart*` until V0196 moved parts onto the
 * service.)
 *
 * To bring it back, both sides together:
 *   1. `VITE_FEATURE_TECH_PART_REQUESTS=true` in the ROOT `.env.local`
 *      (`vite.config.ts` sets `envDir` to the repo root — a per-app
 *      `.env.local` is not read)
 *   2. `app.features.partRequests` true in `services/api` application.yml.
 *      There is no `technicianAddParts` flag any more; it was retired with
 *      the grant, because a flag that reopens a write path with no
 *      authorisation check behind it is worse than no flag.
 *
 * Careful with step 1: `scripts/sync-env.sh` REWRITES the root `.env.local`
 * wholesale (`cat >`, not append) from `/.env`, so a var hand-added there is
 * gone the next time anyone syncs. Either teach the script to emit it from
 * `/.env` alongside `VITE_VOLLGAS_DEV_MODE`, or re-add it after every sync.
 */
export const FEATURE_TECH_PART_REQUESTS =
  import.meta.env.VITE_FEATURE_TECH_PART_REQUESTS === 'true'

/**
 * Technician-attached catalog SERVICES — **withheld from this release.**
 *
 * Same shape as the parts decision above, and the same reasoning: a
 * technician who finds more work walks over and tells the manager, who
 * adds the line. What a customer is asked to pay for does not get onto
 * the job from the floor.
 *
 * Hides the "+ Additional service" button on the work-order detail. The
 * picker, its hook and its tests remain.
 *
 * Note: attaching a catalog service from the floor is gone outright, not
 * merely hidden — the technician `POST .../services` route went with the
 * DIRECT_ADD grant. This flag hides the "+ Additional service" button;
 * `proposeService` is what a technician uses instead, and it is not gated.
 */
export const FEATURE_TECH_ADD_SERVICE =
  import.meta.env.VITE_FEATURE_TECH_ADD_SERVICE === 'true'
